/**
 * Hook for fetching and caching device online/offline statuses
 * Uses the optimized batch endpoint from backend API
 * Makes parallel requests for better performance with large device counts
 * Uses React Query for automatic deduplication and caching
 * Persists status data to localStorage for immediate rendering on refresh
 */

import { useQuery } from '@tanstack/react-query'
import { useEffect, useMemo, useRef, useState } from 'react'
import { api } from '../services'
import { DeviceInfo } from '../common/enums'
import { DeviceStatus, DEVICE_ONLINE_THRESHOLD_MINUTES } from '../utils/deviceStatus'

/**
 * How long the device list must stop changing before its statuses are fetched.
 * Long enough to span the gap between admin pagination pages, short enough that
 * a settled list is not visibly delayed.
 */
const DEVICE_ID_SETTLE_MS = 500

/**
 * Return `value`, but only once it has stopped changing for `delayMs`.
 *
 * Compared by content, not identity, so a list rebuilt with the same members
 * never re-triggers. The first non-empty value is adopted immediately: waiting
 * on a list that only ever arrives once would delay every caller for no reason.
 */
function useSettledValue(value: string[], delayMs: number): string[] {
  const [settled, setSettled] = useState<string[]>(value)
  const settledRef = useRef(settled)
  settledRef.current = settled

  const signature = value.join(',')

  useEffect(() => {
    if (signature === settledRef.current.join(',')) return
    if (settledRef.current.length === 0) {
      setSettled(signature ? signature.split(',') : [])
      return
    }
    const timeoutId = setTimeout(() => setSettled(signature ? signature.split(',') : []), delayMs)
    return () => clearTimeout(timeoutId)
    // Tracked through the content signature rather than `value`, whose array
    // identity changes on every render of the caller.
  }, [signature, delayMs])

  return settled
}

/**
 * LocalStorage key for persisting device statuses
 */
const STORAGE_KEY = 'ioag_device_statuses_cache'

/**
 * How long a cached status map is trusted for the *initial paint* on refresh.
 * We deliberately keep this long (24h): showing a device's last-known
 * online/offline state for the ~second it takes any refetch to return is far
 * better than flashing every device as offline. The cache is fed to React
 * Query with its real age (initialDataUpdatedAt: cache timestamp), so a mount
 * only triggers a network refetch when the cache is older than staleTime.
 */
const CACHE_MAX_AGE_MS = 24 * 60 * 60 * 1000

/**
 * Load cached device statuses from localStorage
 * Returns both the data and timestamp for React Query
 */
function loadCachedStatuses(): { data: { [key: string]: any } | null; timestamp: number | null } {
  try {
    const cached = localStorage.getItem(STORAGE_KEY)
    if (!cached) return { data: null, timestamp: null }

    const parsed = JSON.parse(cached)
    const now = Date.now()

    // Drop only very old caches; keep recent-enough data for instant render.
    if (parsed.timestamp && now - parsed.timestamp > CACHE_MAX_AGE_MS) {
      localStorage.removeItem(STORAGE_KEY)
      return { data: null, timestamp: null }
    }

    return {
      data: parsed.data || null,
      timestamp: parsed.timestamp || null,
    }
  } catch (error) {
    console.warn('[Device Status] Error loading cached statuses from localStorage:', error)
    return { data: null, timestamp: null }
  }
}

/**
 * Merge freshly-fetched statuses into the cached map and persist.
 *
 * Several components call this hook with different device subsets (map,
 * sidebar, dashboard, mobile drawer). Replacing the cache with one caller's
 * subset would clobber the others and leave most devices uncached on the next
 * refresh — which is exactly what makes the list flash to all-offline. Merging
 * lets the cache accumulate a complete picture across callers and page loads.
 */
function saveCachedStatuses(data: { [key: string]: any }): void {
  try {
    const existing = loadCachedStatuses().data || {}
    const merged = { ...existing, ...data }
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({
        data: merged,
        timestamp: Date.now(),
      })
    )
  } catch (error) {
    console.warn('[Device Status] Error saving statuses to localStorage:', error)
  }
}

/**
 * Maximum device IDs to send in a single /devices-status request.
 * Backend caps non-admin callers at 100; admins at 1000. Keep this at 100 so
 * the same client logic works for every role and we always stay safely under
 * the lower cap. Larger device sets are split across parallel requests.
 */
const MAX_DEVICES_PER_REQUEST = 100

/**
 * Split array into chunks of at most `chunkSize` items.
 */
function chunkArray<T>(array: T[], chunkSize: number): T[][] {
  if (array.length === 0) return []
  const size = Math.max(1, chunkSize)
  const result: T[][] = []
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size))
  }
  return result
}

export interface DeviceStatusMap {
  [sigfoxId: string]: {
    status: DeviceStatus
    lastReadingTimestamp?: number
    loading: boolean
    error?: string
  }
}

interface UseDeviceStatusesResult {
  deviceStatuses: DeviceStatusMap
  isLoading: boolean
  error: string | null
  refetch: () => void
}

/**
 * Fetch device statuses from API
 * This function is called by React Query and handles the actual API calls
 */
async function fetchDeviceStatuses(deviceIds: string[], thresholdMinutes: number): Promise<{ [key: string]: any }> {
  if (!deviceIds || deviceIds.length === 0) {
    return {}
  }

  const mergedStatuses: { [key: string]: any } = {}

  // Split into chunks no larger than MAX_DEVICES_PER_REQUEST and fetch in parallel.
  const deviceIdChunks = chunkArray(deviceIds, MAX_DEVICES_PER_REQUEST)
  const responses = await Promise.all(
    deviceIdChunks.map((chunk) => api.getDevicesStatus(chunk, thresholdMinutes))
  )

  responses.forEach((response) => {
    if (response && response.statuses) {
      Object.assign(mergedStatuses, response.statuses)
    }
  })

  return mergedStatuses
}

/**
 * Hook to fetch and manage device statuses for multiple devices
 * Uses React Query for automatic deduplication, caching, and background refetching
 */
export function useDeviceStatuses(
  devices: DeviceInfo[],
  thresholdMinutes: number = DEVICE_ONLINE_THRESHOLD_MINUTES
): UseDeviceStatusesResult {
  // Create stable device IDs array for React Query key
  const deviceIds = useMemo(() => devices.map((d) => d.sigfoxId).sort(), [devices])

  // Admins receive their device list one page at a time, and each page grows
  // this array — which is the query key, so every page re-fetched the status of
  // every device accumulated so far. On a ~560 device tenant that was 6 pages
  // fanning out to 21 requests. Settling the list first collapses it to one
  // fan-out; the localStorage cache still paints statuses immediately.
  const settledDeviceIds = useSettledValue(deviceIds, DEVICE_ID_SETTLE_MS)

  // Load cached data from localStorage for immediate rendering
  const cachedResult = useMemo(() => {
    return loadCachedStatuses()
  }, [])

  // Use React Query to fetch and cache device statuses
  // This automatically handles deduplication across multiple components
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['device-statuses', settledDeviceIds, thresholdMinutes],
    queryFn: async () => {
      const result = await fetchDeviceStatuses(settledDeviceIds, thresholdMinutes)
      // Save to localStorage for next page load
      saveCachedStatuses(result)
      return result
    },
    staleTime: 5 * 60 * 1000, // 5 minutes - data is fresh for this long
    gcTime: 10 * 60 * 1000, // 10 minutes - keep in cache
    refetchOnWindowFocus: false, // Don't refetch on every tab focus - device status changes slowly (6h offline threshold)
    refetchInterval: 5 * 60 * 1000, // Auto-refetch every 5 minutes (aligned with staleTime)
    refetchIntervalInBackground: false, // Pause polling while the tab is hidden
    enabled: settledDeviceIds.length > 0, // Only run query if we have devices
    placeholderData: (previousData) => previousData, // Use React Query cache immediately
    initialData: cachedResult.data, // Use localStorage cache on first mount
    // Trust the localStorage cache for its real age instead of forcing a refetch
    // on every mount. React Query will refetch on mount only if the cache is
    // already older than staleTime; a fresh cache renders with no network call.
    initialDataUpdatedAt: cachedResult.timestamp ?? 0,
  })

  // Transform API response into DeviceStatusMap format
  const deviceStatuses = useMemo(() => {
    const statusMap: DeviceStatusMap = {}

    devices.forEach((device) => {
      if (data && data[device.sigfoxId]) {
        // Backend returned status for this device (either from cache or fresh API response)
        statusMap[device.sigfoxId] = {
          status: data[device.sigfoxId].status as DeviceStatus,
          lastReadingTimestamp: data[device.sigfoxId].lastReadingTimestamp,
          loading: isLoading, // Show loading state during background refetch, but still display cached data
        }
      } else if (isLoading) {
        // Still loading and no cached data available for this device
        statusMap[device.sigfoxId] = {
          status: 'pending',
          loading: true,
        }
      } else {
        // Backend didn't return status for this device
        statusMap[device.sigfoxId] = {
          status: 'pending',
          loading: false,
          error: 'Status not available',
        }
      }
    })

    return statusMap
  }, [devices, data, isLoading])

  return {
    deviceStatuses,
    isLoading,
    error: error ? (error as Error).message : null,
    refetch,
  }
}
